import React from "react";
import { Box, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Dropdown from "../Dropdown/Dropdown";
import InputField from "../InputField/InputField";
import IconButton from "../Button/Button";
import { styled } from "@mui/material/styles";

const StyledResourceBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  transition: "all 0.2s ease",
  "& .MuiInputBase-root": {
    height: "32px",
    transition: "all 0.2s ease",
  },
}));

const ActionIconButton = styled(IconButton)(({ theme, isAdd }) => ({
  minWidth: "auto",
  width: "24px",
  height: "24px",
  padding: 0,
  color: isAdd ? theme.palette.primary.main : theme.palette.error.main,
  borderColor: "transparent",
  backgroundColor: isAdd
    ? theme.palette.primary.lighter
    : theme.palette.error.lighter,
  "&:hover": {
    borderColor: "transparent",
    backgroundColor: isAdd
      ? theme.palette.primary.light
      : theme.palette.error.light,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "16px",
  },
}));

const ResourceAllocation = ({
  value = [],
  onChange,
  fieldConfig,
  getOptionsForField,
  isNew = true,
  unavailableResourceIds = [],
  budgetHours = 0,
}) => {
  const formatTimeValue = (value) => {
    if (!value || value === "0") return "0";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "0";

    // Convert decimal hours to hours and minutes
    const hours = Math.floor(numValue);
    const minutes = Math.round((numValue - hours) * 60); // Convert decimal to minutes (multiply by 60)

    // Convert back to decimal format with proper precision
    const decimalHours = hours + (minutes / 60);
    return decimalHours.toFixed(2);
  };

  const parseTimeInput = (value) => {
    if (!value) return "";
    const regex = /^(?:\d*\.?\d{0,2}|\.\d{0,2})$/;
    if (!regex.test(value)) return "";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "";

    // Convert decimal hours to hours and minutes
    const hours = Math.floor(numValue);
    const minutes = Math.round((numValue - hours) * 60); // Convert decimal to minutes

    // Convert back to decimal format with proper precision
    const decimalHours = hours + (minutes / 60);
    return decimalHours.toFixed(2);
  };

  const [localValue, setLocalValue] = React.useState(() => {
    if (Array.isArray(value) && value.length > 0) {
      return value.map((item) => ({
        resource_id: item.resource_id?.toString() || "",
        allocation_hours:
          item.budget_hours !== undefined &&
          item.budget_hours !== null &&
          item.budget_hours !== ""
            ? item.budget_hours.toString()
            : "0",
        resource_actual_hours: item.resource_actual_hours,
      }));
    }

    return [
      {
        resource_id: "",
        allocation_hours: "",
        resource_actual_hours: null,
      },
    ];
  });

  const totalAllocatedHours = React.useMemo(() => {
    const total = localValue.reduce((sum, item) => {
      const hours = parseFloat(item.allocation_hours) || 0;
      return sum + hours;
    }, 0);
    return Number(total.toFixed(2)); // Ensure consistent precision
  }, [localValue]);

  const resourceOptions = React.useMemo(() => {
    const options = getOptionsForField?.("resources") || [];
    return options;
  }, [getOptionsForField]);

  const isResourceUnavailable = React.useCallback(
    (resourceId) => {
      return unavailableResourceIds.includes(parseInt(resourceId));
    },
    [unavailableResourceIds]
  );

  React.useEffect(() => {
    if (Array.isArray(value)) {
      const formattedValue =
        value.length > 0
          ? value.map((item) => ({
              resource_id: item.resource_id?.toString() || "",
              allocation_hours:
                item.allocation_hours !== undefined &&
                item.allocation_hours !== null &&
                item.allocation_hours !== ""
                  ? item.allocation_hours.toString()
                  : "",
              resource_actual_hours: item.resource_actual_hours,
            }))
          : [
              {
                resource_id: "",
                allocation_hours: "",
                resource_actual_hours: null,
              },
            ];
      setLocalValue(formattedValue);
    }
  }, [value]);

  const handleAddResource = React.useCallback(() => {
    const newValue = [
      ...localValue,
      {
        resource_id: "",
        allocation_hours: "",
        resource_actual_hours: null,
      },
    ];
    setLocalValue(newValue);
    onChange(newValue);
  }, [localValue, onChange]);

  const handleRemoveResource = (index) => {
    const newValue = localValue.filter((_, i) => i !== index);
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleFieldChange = (index, fieldName, fieldValue) => {
    const newValue = [...localValue];
    if (fieldName === "allocation_hours") {
      // Allow empty value or valid decimal input
      if (fieldValue === "") {
        newValue[index] = {
          ...newValue[index],
          [fieldName]: "",
        };
        setLocalValue(newValue);
        onChange(newValue);
        return;
      }

      // Allow direct decimal input like .1 or 0.1
      if (/^[0-9]*\.?[0-9]*$/.test(fieldValue)) {
        newValue[index] = {
          ...newValue[index],
          [fieldName]: fieldValue,
        };
        setLocalValue(newValue);
        onChange(newValue);
      }
    } else {
      newValue[index] = {
        ...newValue[index],
        [fieldName]: fieldValue?.toString() || "",
      };
      setLocalValue(newValue);
      onChange(newValue);
    }
  };

  const getFilteredOptions = (currentIndex) => {
    const selectedResources = localValue
      .map((item) => item.resource_id)
      .filter((id, idx) => idx !== currentIndex && id !== "");

    return resourceOptions.filter(
      (option) => !selectedResources.includes(option.value?.toString())
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {localValue.map((resource, index) => {
        const isUnavailable = isResourceUnavailable(resource.resource_id);
        const filteredOptions = getFilteredOptions(index);

        return (
          <StyledResourceBox key={index}>
            <Tooltip
              title={index === 0 ? "Add Resource" : "Remove Resource"}
              arrow
            >
              <ActionIconButton
                variant="contained"
                size="small"
                isAdd={index === 0}
                onClick={
                  index === 0
                    ? handleAddResource
                    : () => handleRemoveResource(index)
                }
              >
                {index === 0 ? <AddIcon /> : <RemoveIcon />}
              </ActionIconButton>
            </Tooltip>

            <Box sx={{ flex: 1 }}>
              <Dropdown
                value={resource.resource_id}
                onChange={(value) =>
                  handleFieldChange(index, "resource_id", value)
                }
                options={filteredOptions}
                placeholder="Please select Members"
                fullWidth
                size="small"
                disabled={resource.resource_actual_hours !== null && resource.resource_actual_hours > 0}
                error={isUnavailable}
                sx={{
                  "& .MuiOutlinedInput-root": {
                                      backgroundColor:
                    resource.resource_actual_hours !== null && resource.resource_actual_hours > 0
                      ? "action.disabledBackground"
                      : "transparent",
                    "& fieldset": {
                      borderColor: isUnavailable ? "error.main" : "divider",
                    },
                    "&:hover fieldset": {
                      borderColor: isUnavailable
                        ? "error.main"
                        : "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: isUnavailable
                        ? "error.main"
                        : "primary.main",
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ width: "100px" }}>
              <InputField
                value={resource.allocation_hours}
                onChange={(e) => {
                  const value = e.target.value;
                  handleFieldChange(index, "allocation_hours", value);
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    handleFieldChange(index, "allocation_hours", "0");
                    return;
                  }
                  if (value) {
                    const formattedValue = formatTimeValue(value);
                    handleFieldChange(
                      index,
                      "allocation_hours",
                      formattedValue
                    );
                  }
                }}
                type="text"
                placeholder="0"
                endAdornment="hrs"
                error={isUnavailable || totalAllocatedHours > budgetHours}
                inputProps={{
                  pattern: "^(?:\\d*\\.?\\d{0,2}|\\.\\d{0,2})$",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "transparent",
                    "& fieldset": {
                      borderColor:
                        isUnavailable || totalAllocatedHours > budgetHours
                          ? "error.main"
                          : "divider",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        isUnavailable || totalAllocatedHours > budgetHours
                          ? "error.main"
                          : "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        isUnavailable || totalAllocatedHours > budgetHours
                          ? "error.main"
                          : "primary.main",
                    },
                  },
                  "& input": {
                    textAlign: "right",
                  },
                }}
              />
            </Box>
          </StyledResourceBox>
        );
      })}

      <Box
        sx={{
          mt: 1,
          fontSize: "0.75rem",
          color:
            totalAllocatedHours > budgetHours
              ? "error.main"
              : totalAllocatedHours === budgetHours
              ? "success.main"
              : "text.secondary",
          fontStyle: "italic",
        }}
      >
        {totalAllocatedHours > budgetHours
          ? `Warning: You've exceeded the budget by ${Number(
              totalAllocatedHours - budgetHours
            ).toFixed(1)} hours`
          : totalAllocatedHours === budgetHours
          ? "Perfect! Your allocation matches the budget exactly."
          : totalAllocatedHours > 0
          ? `You still have ${Number(budgetHours - totalAllocatedHours).toFixed(
              1
            )} hours left to allocate`
          : "Please allocate hours to resources"}
      </Box>
    </Box>
  );
};

export default ResourceAllocation;
